import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import apiClient from "../../services/apiClient";
import { useAuth } from "../../services/AuthContext";

const SuperadminEntitiesPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth() as any;
  const [companies, setCompanies] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isSuperadmin = useMemo(
    () => Array.isArray(user?.roles) && user.roles.includes("superadmin"),
    [user?.roles],
  );

  const loadCompanies = useCallback(async () => {
    try {
      setError(null);
      const response = await apiClient.get("/api/companies");
      setCompanies(response.companies || []);
    } catch (e: any) {
      setError(e?.message || t("superadminEntities.loadError"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  if (!isSuperadmin) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{t("superadminEntities.accessDenied")}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box
        sx={{
          mb: 3,
          display: "flex",
          justifyContent: "space-between",
          gap: 2,
        }}>
        <Typography variant="h3" fontWeight={700}>
          {t("superadminEntities.title")}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate("/superadmin/entities/new")}>
          {t("superadminEntities.create")}
        </Button>
      </Box>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t("superadminEntities.columns.name")}</TableCell>
              <TableCell>{t("superadminEntities.columns.siret")}</TableCell>
              <TableCell>{t("superadminEntities.columns.currency")}</TableCell>
              <TableCell>{t("superadminEntities.columns.city")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {companies.map((company) => (
              <TableRow
                key={company.id}
                hover
                sx={{ cursor: "pointer" }}
                onClick={() => navigate(`/superadmin/entities/${company.id}`)}>
                <TableCell>
                  {company.trade_name || company.legal_name}
                </TableCell>
                <TableCell>{company.siret}</TableCell>
                <TableCell>{company.base_currency}</TableCell>
                <TableCell>{company.address?.city || "-"}</TableCell>
              </TableRow>
            ))}
            {!loading && companies.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4}>{t("superadminEntities.empty")}</TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

export default SuperadminEntitiesPage;
